import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useMediaLimitsIndication } from '@app/modules/social-export/hooks/useMediaLimitsIndication';
import { useStationPlatformConnections } from '@app/modules/social-export/hooks/useStationPlatformConnections';
import { MediaLimitsShort } from '@app/modules/social-export/publish';
import { Box } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useWatch } from 'react-hook-form-mui';
import { PublishToggleCard } from './PublishToggleCard';
import { QuickPlatformUploadDetails } from './QuickPlatformUploadDetails';

/**
 * Toggle card for quick publish on a single platform.
 * Parent is responsible for only rendering when the station has a connection for this platform.
 */
export function QuickPlatformUploadFields({
  stationId,
  platform,
  durationMs,
}: {
  stationId: string;
  platform: SocialPlatform;
  durationMs?: number;
}) {
  const { setValue } = useFormContext();
  const fieldPrefix = `platformUpload.${platform.id}`;
  const isOn = useWatch({ name: `${fieldPrefix}.enabled` });
  const selectedConnectionId = useWatch({ name: `${fieldPrefix}.connectionId` }) as
    | string
    | undefined;
  const { connections } = useStationPlatformConnections(stationId, platform);
  // Prefer the form-selected account so limits match what will publish; fall back to default.
  const connection = useMemo(
    () => connections.find((c) => c.id === selectedConnectionId) ?? connections[0],
    [connections, selectedConnectionId],
  );
  const indication = useMediaLimitsIndication(connection, durationMs);
  const isBlocked = Boolean(indication?.blocksPublish);

  useEffect(() => {
    if (isBlocked) {
      setValue(`${fieldPrefix}.enabled`, false);
    }
  }, [fieldPrefix, isBlocked, setValue]);

  return (
    <PublishToggleCard
      icon={platform.Icon({ fontSize: 'small', colored: true })}
      label={
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {platform.name}
          {indication ? <MediaLimitsShort indication={indication} placement="top" /> : null}
        </Box>
      }
      switchName={`${fieldPrefix}.enabled`}
      disabled={isBlocked}
    >
      {isOn && <QuickPlatformUploadDetails stationId={stationId} platform={platform} />}
    </PublishToggleCard>
  );
}
